import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

type AdminUser = { id: string; email: string; name: string; role: string };

type AuthState = {
    status: "checking" | "authed" | "guest";
    user: AdminUser | null;
    login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
};

const Ctx = createContext<AuthState | null>(null);

function formatDetail(detail: unknown): string {
    if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof (e as { msg?: string }).msg === "string" ? (e as { msg: string }).msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof (detail as { msg?: string }).msg === "string")
        return (detail as { msg: string }).msg;
    return String(detail);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [status, setStatus] = useState<AuthState["status"]>("checking");
    const [user, setUser] = useState<AdminUser | null>(null);

    const refresh = useCallback(async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
                credentials: "include",
            });
            if (res.ok) {
                const u = (await res.json()) as AdminUser;
                setUser(u);
                setStatus("authed");
            } else {
                setUser(null);
                setStatus("guest");
            }
        } catch {
            setUser(null);
            setStatus("guest");
        }
    }, []);

    useEffect(() => {
        // Skip auth check on purely public routes to avoid noisy 401 console entries.
        if (typeof window !== "undefined") {
            const p = window.location.pathname;
            if (!p.startsWith("/admin")) {
                setStatus("guest");
                setUser(null);
                return;
            }
        }
        void refresh();
    }, [refresh]);

    const login = useCallback(
        async (email: string, password: string) => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email, password }),
                });
                const data = await res.json();
                if (!res.ok) {
                    return { ok: false, error: formatDetail(data?.detail) };
                }
                setUser(data.user as AdminUser);
                setStatus("authed");
                return { ok: true };
            } catch (e) {
                return { ok: false, error: (e as Error).message };
            }
        },
        [],
    );

    const logout = useCallback(async () => {
        try {
            await fetch(`${BACKEND_URL}/api/auth/logout`, {
                method: "POST",
                credentials: "include",
            });
        } catch {
            /* ignore */
        }
        setUser(null);
        setStatus("guest");
    }, []);

    const value = useMemo<AuthState>(
        () => ({ status, user, login, logout, refresh }),
        [status, user, login, logout, refresh],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
}

/** fetch mit credentials + JSON error handling + exponentiellem Backoff
 * für transiente HTTP-Fehler (429 / 5xx). Bis zu 3 Retries mit steigenden
 * Delays (~1s, 3s, 8s). Bewahrt die App vor „HTTP 429"-Balken bei
 * kurzzeitiger Edge-/Ingress-Drosselung. */
const _API_RETRYABLE = new Set([408, 425, 429, 500, 502, 503, 504]);
const _API_RETRY_DELAYS_MS = [1000, 3000, 8000];

function _apiSleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
}

export async function api<T = unknown>(
    path: string,
    init?: RequestInit,
    opts?: { retry?: boolean; aggressiveRetry?: boolean },
): Promise<{ ok: boolean; status: number; data: T | null; error?: string }> {
    // Nur idempotente Methoden dürfen still retryed werden. POST/PATCH/DELETE
    // bekommen genau einen Versuch, damit wir keine Duplikate erzeugen —
    // außer der Caller opted explizit via `opts.retry` ein (z. B. für
    // idempotente Multipart-Complete-Calls). `aggressiveRetry` nutzt lange
    // Delays (5s/15s/30s/60s) für kritische Finalize-Endpoints.
    const method = (init?.method || "GET").toUpperCase();
    const isIdempotent = method === "GET" || method === "HEAD";
    const canRetry = isIdempotent || !!opts?.retry;
    const delays = opts?.aggressiveRetry ? [5000, 15000, 30000, 60000] : _API_RETRY_DELAYS_MS;
    let lastStatus = 0;
    let lastErr: string | undefined;
    const maxAttempts = canRetry ? delays.length : 0;
    for (let attempt = 0; attempt <= maxAttempts; attempt++) {
        try {
            const res = await fetch(`${BACKEND_URL}${path}`, {
                credentials: "include",
                ...init,
            });
            const ct = res.headers.get("content-type") || "";
            let data: unknown = null;
            if (ct.includes("application/json")) {
                data = await res.json();
            }
            if (!res.ok) {
                if (_API_RETRYABLE.has(res.status) && attempt < maxAttempts) {
                    console.warn(`[api] ${path} → ${res.status}, retry #${attempt + 1} in ${delays[attempt]}ms`);
                    await _apiSleep(delays[attempt]);
                    continue;
                }
                // Bei transienten Statuscodes (429, 503, 504) IMMER die
                // friendly-message priorisieren — der Body kann roh ein
                // "Too Many Requests" oder HTML sein und ist für den User
                // nutzlos.
                const useFriendly = res.status === 429 || res.status === 503 || res.status === 504 || res.status === 502 || res.status === 408 || res.status === 425;
                const err = useFriendly
                    ? friendlyStatusMessage(res.status)
                    : (data && typeof data === "object" && "detail" in (data as Record<string, unknown>)
                        ? formatDetail((data as { detail: unknown }).detail)
                        : friendlyStatusMessage(res.status));
                return { ok: false, status: res.status, data: null, error: err };
            }
            return { ok: true, status: res.status, data: data as T };
        } catch (e) {
            lastStatus = 0;
            lastErr = (e as Error).message;
            if (attempt < maxAttempts) {
                await _apiSleep(delays[attempt]);
                continue;
            }
            return { ok: false, status: 0, data: null, error: lastErr };
        }
    }
    return { ok: false, status: lastStatus, data: null, error: lastErr };
}

/** Übersetzt HTTP-Statuscodes in nutzerfreundliche deutsche Meldungen. */
function friendlyStatusMessage(status: number): string {
    switch (status) {
        case 401:
            return "Sitzung abgelaufen. Bitte erneut anmelden.";
        case 403:
            return "Keine Berechtigung.";
        case 404:
            return "Nicht gefunden.";
        case 408:
        case 425:
        case 429:
            return "Server ist gerade sehr beschäftigt. Bitte einen Moment warten.";
        case 500:
        case 502:
        case 503:
        case 504:
            return "Server ist gerade nicht erreichbar. Bitte erneut versuchen.";
        default:
            return `Ein Fehler ist aufgetreten (Code ${status}).`;
    }
}
